import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { InstrumentDefinitionDto, ProjectDto } from '@app/shared/models/contracts/project-dto';
import {
  cloneToMutableData,
  enumKeyByValue,
  getProjectDraftWithAddedDlom,
  getProjectDraftWithRemovedDlom,
} from '@app/shared/helpers';
import * as projectActions from '@core/store/project-slice';
import styles from './payout-logic.module.scss';
import classNames from 'classnames';
import Button from '@app/shared/components/button/Button';
import PlusSvg from '@app/shared/icons/Plus';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { createPayoutLogicItem } from '@app/shared/helpers/create-tranche';
import { v4 as uuidv4 } from 'uuid';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { MAX_TRANCHES_PER_INSTRUMENT } from '@app/shared/constants/payout-logic';
import SvgEdit from '@app/shared/icons/Edit';
import {
  CapType,
  ContainerTestId,
  Else,
  InstrumentType,
} from '@app/shared/models/contracts/enums/shared-enums';
import SvgTrash from '@app/shared/icons/Trash';
import { useLocale } from '@app/core/hooks/useLocale';
import { RouteConstants } from '../../../../RouteConstants';
import { setPageScrollPosition } from '@app/core/store/ui-values-slice';
import PayoutStatementsErf from './payout-statements/PayoutStatementsErf';
import {
  selectCapitalStructures,
  selectInstruments,
} from '@app/core/store/capital-structure-slice-selectors';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import { Toggle, ToggleSize } from '@app/shared/components/toggle/Toggle';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import FormField, {
  ValidationPlacement,
} from '@app/shared/components/form-controls/form-field/FormField';
import { numericBasicFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import {
  getRequiredValidator,
  greaterThanOrEqualToValidator,
} from '@app/core/validations/hook-forms/validators';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const safeSetProperty = (obj: Record<string, any>, key: string, value: unknown): void => {
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];
  for (const dangerousKey of dangerousKeys) {
    if (key === dangerousKey) {
      throw new Error(`Rejected dangerous property name: ${key}`);
    }
  }

  if (Object.prototype.hasOwnProperty.call(obj, key) || !(key in obj)) {
    Object.assign(obj, { [key]: value });
  } else {
    Object.assign(obj, { [key]: value });
  }
};

const PARENT_CLASSNAME = 'payout-logic';

export const PayoutLogicErf = () => {
  const { instrumentId } = useParams();
  // required to force re-render when instrumentId changes, which is needed to dispatch scroll positions
  return <PayoutLogicComponentErf key={instrumentId} />;
};

const PayoutLogicComponentErf: FC = (): JSX.Element => {
  const { l } = useLocale();
  const { instrumentId } = useParams();
  const dispatch = useAppDispatch();

  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const trancheId = uuidv4();
  const pageScrollPositionList = useAppSelector(
    (state) => state.uiValues.pageScrollPositionList[projectDraft.id]
  );
  const mainContainer = document.querySelector<HTMLElement>(
    `[data-testid=${ContainerTestId.Main}]`
  );
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollTopRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const activeInstrument = cloneToMutableData(
    useAppSelector(selectInstruments).find((x) => x.instrumentId === instrumentId)
  );
  const [currentEquityId, setCurrentEquityId] = useState<string | undefined>(
    activeInstrument?.instrumentId
  );

  const [selectedRadio, setSelectedRadio] = useState<Nullable<CapType>>(
    activeInstrument?.capType ?? null
  );

  const formMethods = useForm<InstrumentDefinitionDto>({
    ...formConfigBase,
    defaultValues: {
      ...{ ...activeInstrument },
    },
  });

  const { trigger, setValue } = formMethods;

  const isDistributionCapEnabled = selectedRadio !== null;
  const isInstrumentCapSelected = selectedRadio === CapType.Instrument;
  const isTrancheCapSelected = selectedRadio === CapType.Tranche;
  const isAtLeastOneTrancheCapValuePresent = activeInstrument?.payoutLogic?.some(
    (tranche) => tranche.capValue !== null
  );

  const instruments = useAppSelector(selectInstruments);
  const otherInstruments = instruments.filter(
    (inst) =>
      inst.instrumentId !== instrumentId &&
      inst.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
  );
  const isAllOtherInstrumentsCapped = otherInstruments.every((inst) => inst.capType !== null);
  const isCapToggleDisabled = isAllOtherInstrumentsCapped && !activeInstrument?.capType;

  useEffect(() => {
    scrollTopRef.current = scrollTop;
    scrollLeftRef.current = scrollLeft;
  }, [scrollTop, scrollLeft]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(mainContainer?.scrollTop ?? 0);
      setScrollLeft(mainContainer?.scrollLeft ?? 0);
    };

    if (mainContainer) {
      mainContainer.addEventListener('scroll', handleScroll);
    }

    if (pageScrollPositionList) {
      const pageScrollPosition = pageScrollPositionList.find(
        (page) => page.pageName === RouteConstants.PayoutLogic + instrumentId
      );
      mainContainer &&
        pageScrollPosition &&
        mainContainer.scrollTo(pageScrollPosition.scrollLeft, pageScrollPosition.scrollTop);
    }
    return () => {
      const actionPayload = {
        key: projectDraft.id,
        value: {
          pageName: RouteConstants.PayoutLogic + instrumentId,
          scrollTop: scrollTopRef.current ?? 0,
          scrollLeft: scrollLeftRef.current ?? 0,
        },
      };
      dispatch(setPageScrollPosition(actionPayload));

      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTriggerEffect(trigger);

  useEffect(() => {
    if (instrumentId !== currentEquityId) {
      setCurrentEquityId(instrumentId);
      formMethods.reset(activeInstrument);
    }
  }, [instrumentId, activeInstrument, currentEquityId, formMethods]);

  const submitForm = async (
    getDraftWithSideEffects: (data: {
      draft: ProjectDto;
      instrumentId: string;
      trancheId?: string;
    }) => ProjectDto = ({ draft }) => draft,
    trancheId?: string
  ) => {
    if (!instrumentId) {
      return;
    }

    const capitalStructuresClone = cloneToMutableData(capitalStructures);
    const capitalStructure = Object.values(capitalStructuresClone)[0];
    const instrumentDefinitions = capitalStructure.instrumentDefinitions;

    const formData = formMethods.getValues();

    safeSetProperty(instrumentDefinitions, instrumentId, {
      ...formData,
      capType: formData.payoutLogic?.length === 0 ? null : formData.capType,
      capValue: formData.payoutLogic?.length === 0 ? null : formData.capValue,
      payoutLogic: formData.payoutLogic
        ? [
            ...formData.payoutLogic
              .sort((a, b) => a.order - b.order)
              .map((tranche, index) => {
                return { ...tranche, order: index };
              }),
          ]
        : [],
    });

    // need to clone the object otherwise we get some read-only properties when it's
    // used to set the form values - see https://github.com/orgs/react-hook-form/discussions/3715#discussioncomment-746931
    const updatedProjectDraft = {
      ...projectDraft,
      capitalStructures: cloneToMutableData(capitalStructuresClone),
    };

    const updatedProjectDraftWithDlom = getDraftWithSideEffects({
      draft: updatedProjectDraft,
      instrumentId,
      trancheId,
    });

    await dispatch(
      projectActions.updateProjectDraft({ project: updatedProjectDraftWithDlom })
    ).unwrap();
  };

  const formSubmitHandler = () => {
    submitForm();
  };

  const {
    append: appendTranche,
    remove: removeTranche,
    fields: tranchesFields,
  } = useFieldArray({
    name: 'payoutLogic',
    keyName: 'trancheKey',
    control: formMethods.control,
  });

  const createTranche = async () => {
    const lastTrancheItem = activeInstrument?.payoutLogic?.length
      ? activeInstrument.payoutLogic[activeInstrument.payoutLogic.length - 1]
      : undefined;

    const trancheItem = lastTrancheItem
      ? {
          ...lastTrancheItem,
          id: trancheId,
          else: enumKeyByValue(Else, Else.Zero),
        }
      : createPayoutLogicItem(0, trancheId);

    await appendTranche(trancheItem, { shouldFocus: false });
    await submitForm(getProjectDraftWithAddedDlom, trancheItem.id);
  };

  const removeTrancheItem = async (trancheIndex: number, trancheId: string) => {
    await removeTranche(trancheIndex);
    await submitForm(getProjectDraftWithRemovedDlom, trancheId);
  };

  // required to prevent leave site dialogue on enter key press
  const submitHandler = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        data-cy="payout-logic-form"
        className={styles[`${PARENT_CLASSNAME}`]}
        onBlur={(event) => {
          if (event.target.type !== 'radio') {
            formMethods.handleSubmit(
              formSubmitHandler as SubmitHandler<InstrumentDefinitionDto>,
              formSubmitHandler as SubmitErrorHandler<InstrumentDefinitionDto>
            )(event);
          }
        }}
        onSubmit={submitHandler}>
        {tranchesFields.length ? (
          <>
            <div className={classNames(styles[`${PARENT_CLASSNAME}__cap-controls`])}>
              <div className={styles[`${PARENT_CLASSNAME}__toggle`]}>
                {isCapToggleDisabled ? (
                  <Tooltip
                    content={l('_CapAtLeastOneOrdinaryEquityInstrumentMustRemainUncapped')}
                    placement={TooltipPlacement.Right}
                    className={styles['project-tooltip-position']}>
                    <Toggle
                      label={l('_ApplyExitDistributionCap')}
                      name="payoutLogicCapEnabled"
                      toggleSize={ToggleSize.Small}
                      labelTextClassName={styles[`${PARENT_CLASSNAME}__toggle-title`]}
                      forceChecked={isDistributionCapEnabled}
                      disabled
                      onChange={(e) => {
                        const newValue = e.target.checked ? CapType.Instrument : null;
                        formMethods.setValue('capType', newValue);
                        setSelectedRadio(newValue);
                        formSubmitHandler();
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Toggle
                    label={l('_ApplyExitDistributionCap')}
                    name="payoutLogicCapEnabled"
                    toggleSize={ToggleSize.Small}
                    labelTextClassName={styles[`${PARENT_CLASSNAME}__toggle-title`]}
                    forceChecked={isDistributionCapEnabled}
                    onChange={(e) => {
                      const newValue = e.target.checked ? CapType.Instrument : null;
                      formMethods.setValue('capType', newValue);
                      setSelectedRadio(newValue);
                      formSubmitHandler();
                    }}
                  />
                )}
              </div>
              {isDistributionCapEnabled && (
                <>
                  <div className={styles[`${PARENT_CLASSNAME}__radio-wrapper`]}>
                    <RadioGroup row key="capType" name="capType" value={selectedRadio}>
                      <FormControlLabel
                        value={CapType.Instrument}
                        control={<Radio size="small" />}
                        label={l('_CapPerInstrument')}
                        data-testid={l('_CapPerInstrument') + ' radio'}
                        classes={{
                          label: styles[`${PARENT_CLASSNAME}__radio-label`],
                        }}
                        sx={{
                          '.MuiButtonBase-root:hover': {
                            bgcolor: 'unset',
                          },
                        }}
                        onChange={() => {
                          setSelectedRadio(CapType.Instrument);
                          setValue('capType', CapType.Instrument);
                          formSubmitHandler();
                        }}
                      />
                      <FormControlLabel
                        value={CapType.Tranche}
                        control={<Radio size="small" />}
                        label={l('_CapPerTranche')}
                        data-testid={l('_CapPerTranche') + ' radio'}
                        classes={{ label: styles[`${PARENT_CLASSNAME}__radio-label`] }}
                        sx={{
                          '.MuiButtonBase-root:hover': {
                            bgcolor: 'unset',
                          },
                        }}
                        onChange={() => {
                          setSelectedRadio(CapType.Tranche);
                          setValue('capType', CapType.Tranche);
                          formSubmitHandler();
                        }}
                      />
                    </RadioGroup>
                  </div>
                  {isInstrumentCapSelected && (
                    <div className={styles[`${PARENT_CLASSNAME}__cap-value`]}>
                      <FormField
                        name="capValue"
                        label={l('_MaximumPayoutCap')}
                        inlineLabel
                        placeholder="0"
                        validationPlacement={ValidationPlacement.FixedBottom}
                        rules={{ ...getRequiredValidator(), ...greaterThanOrEqualToValidator(0) }}
                        {...numericBasicFieldFormattingProps}
                        decimalPoint={0}
                      />
                    </div>
                  )}
                  {!isAtLeastOneTrancheCapValuePresent && isTrancheCapSelected && (
                    <p className={styles[`${PARENT_CLASSNAME}__tranche-validation-text`]}>
                      {l('_CapTrancheLevelValueRequired')}
                    </p>
                  )}
                </>
              )}
            </div>
            {tranchesFields.map((trancheItem, trancheIndex) => {
              return (
                <div
                  key={trancheItem.trancheKey + 'tranche'}
                  className={
                    trancheIndex === 0
                      ? styles[`${PARENT_CLASSNAME}`]
                      : classNames(styles[`${PARENT_CLASSNAME}`], styles['non-first-tranche'])
                  }>
                  <h2 className={classNames('heading-5', styles[`${PARENT_CLASSNAME}__title`])}>
                    {l('_TrancheN', { n: trancheIndex + 1 })}
                    {isTrancheCapSelected && (
                      <div className={classNames(styles[`${PARENT_CLASSNAME}__cap-value`])}>
                        <FormField
                          name={`payoutLogic.${trancheIndex}.capValue`}
                          label={l('_MaximumPayoutCap')}
                          inlineLabel
                          placeholder="0"
                          validationPlacement={ValidationPlacement.FixedRight}
                          rules={{ ...greaterThanOrEqualToValidator(0) }}
                          {...numericBasicFieldFormattingProps}
                          decimalPoint={0}
                        />
                      </div>
                    )}
                  </h2>
                  <div className={styles[`${PARENT_CLASSNAME}__tranche-action-remove`]}>
                    <div className={styles[`${PARENT_CLASSNAME}__action-circle`]}>
                      <Button
                        appearance={ButtonAppearance.DEFAULT_TERTIARY}
                        aria-label={l('_RemoveTranche')}
                        size={ButtonSize.TABLE_ACTION}
                        startIcon={<SvgTrash />}
                        onBlur={(event) => event.stopPropagation()} // this prevents form from submission it's not needed, since it is submitted with removeTrancheItem method
                        onClick={() => removeTrancheItem(trancheIndex, trancheItem.id)}
                      />
                    </div>
                  </div>
                  <div className={styles[`${PARENT_CLASSNAME}__wrapper`]}>
                    <PayoutStatementsErf
                      trancheIndex={trancheIndex}
                      activeEquityInstrument={activeInstrument}
                      formSubmitHandler={formSubmitHandler}
                    />
                  </div>
                </div>
              );
            })}
            {activeInstrument && activeInstrument.payoutLogic && (
              <div className={styles[`${PARENT_CLASSNAME}__tranche-action`]}>
                <Button
                  appearance={ButtonAppearance.DEFAULT_TERTIARY}
                  size={ButtonSize.TABLE_ACTION}
                  startIcon={<PlusSvg />}
                  disabled={activeInstrument.payoutLogic.length >= MAX_TRANCHES_PER_INSTRUMENT}
                  title={
                    activeInstrument.payoutLogic.length >= MAX_TRANCHES_PER_INSTRUMENT
                      ? l('_MaximumNumberOfTranchesIsReached')
                      : undefined
                  }
                  onBlur={(event) => event.stopPropagation()} // this prevents form from submission it's not needed, since it is submitted with createTranche method
                  onClick={createTranche}>
                  {l('_AddTranche')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className={classNames('heading-5', styles[`${PARENT_CLASSNAME}__title`])}>
              {l('_TrancheN', { n: '1' })}
            </h2>
            <div className={styles[`${PARENT_CLASSNAME}__empty-state-wrapper`]}>
              <div className={styles[`${PARENT_CLASSNAME}__empty-state-message`]}>
                {l('_DistributionIsBasedOnPercentageShareholding')}
              </div>
              <div>
                <Button
                  data-testid="payout-customize"
                  appearance={ButtonAppearance.DEFAULT_PRIMARY}
                  startIcon={<SvgEdit />}
                  onBlur={(event) => event.stopPropagation()} // this prevents form from submission it's not needed, since it is submitted with createTranche method
                  onClick={createTranche}>
                  {l('_Customise')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
